"use client";

import { FC, HTMLAttributes, ReactNode, useEffect, useState } from "react";
import Image from "next/image";
import clsx from "clsx";

import { Carousel } from "@mantine/carousel";

import HtmlText from "../common/HtmlText";

// -----------------------------------------------------------------------------------

export type IInformationSlide = {
  image?: { src: string; alt: string };
  heading?: string;
  subHeading?: string;
  description?: ReactNode;
  variant: "primary" | "secondary" | "primary_dark";
};

export type IInformationSliderSectionProps = HTMLAttributes<HTMLElement> & {
  slides?: IInformationSlide[];
};

const bgColor: {
  primary: string;
  secondary: string;
  primary_dark: string;
} = {
  primary: "bg-primary-100",
  secondary: "bg-secondary-50",
  primary_dark: "bg-primary-950",
};

const InformationSliderSection: FC<IInformationSliderSectionProps> = (
  props,
) => {
  const { slides, className, ...other } = props;

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      slides && setActiveIndex((prevState) => (prevState + 1) % slides.length);
    }, 3000);

    if (isHovered) clearInterval(interval);

    return () => clearInterval(interval);
  }, [activeIndex, isHovered, slides]);

  return (
    <section
      className={clsx("container py-5 lg:py-8 xl:py-[50px]", className)}
      {...other}
    >
      {slides && slides.length > 0 && (
        <div className={clsx("relative", bgColor[slides[activeIndex].variant])}>
          <Carousel
            withControls={false}
            initialSlide={activeIndex}
            draggable={false}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {slides.map((slide, idx) => (
              <Carousel.Slide key={idx}>
                <div
                  className={clsx(
                    "flex flex-col-reverse px-5 py-8 lg:flex-row lg:items-start lg:justify-between lg:px-8 xl:px-16 xl:py-14",
                    bgColor[slide.variant],
                  )}
                >
                  <div className="h-full w-full pt-7 md:w-2/3 lg:pr-5 lg:pt-0">
                    {slide.subHeading && (
                      <span className="mb-4 block text-base font-medium text-secondary-900 lg:mb-7 lg:pt-0 lg:text-base xl:mb-10 xl:text-h6">
                        {slide.subHeading}
                      </span>
                    )}

                    {slide.heading && (
                      <div>
                        <h2
                          className={clsx(
                            "mb-4 text-h5 font-medium text-black-900 lg:mb-5 lg:text-h3 xl:mb-6 xl:text-h2",
                            slide.variant === "primary_dark" && "!text-white",
                          )}
                        >
                          {slide.heading}
                        </h2>
                      </div>
                    )}

                    {slide.description && (
                      <HtmlText
                        className={clsx(
                          "mb-6 lg:mb-10 xl:mb-12",
                          slide.variant === "primary_dark" &&
                            "prose-headings:text-white prose-p:!text-white prose-strong:text-white prose-li:!text-white",
                        )}
                      >
                        {slide.description}
                      </HtmlText>
                    )}
                  </div>

                  {/* Guard on the src, not just the image object: an empty Strapi
                      media relation still yields { src: "", alt: "" } (a truthy
                      object), which would render <Image src=""> as a broken-image
                      icon. Only paint the portrait when a real source exists. */}
                  {slide?.image?.src && (
                    <div className="mx-auto h-[295px] w-[246px] overflow-hidden lg:mx-0">
                      <Image
                        src={slide.image.src}
                        alt={slide.image.alt}
                        height={295}
                        width={246}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </Carousel.Slide>
            ))}
          </Carousel>

          <div className="absolute bottom-4 left-4 flex items-center gap-8 pb-2 pl-2 lg:bottom-8 lg:left-8 xl:bottom-14 xl:left-14">
            {slides.map((_slide, index) => (
              <span
                key={index}
                onClick={() => setActiveIndex(index)}
                className={clsx(
                  "block size-2.5 cursor-pointer rounded-full bg-yellow-400",
                  index === activeIndex && "spin",
                )}
              ></span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default InformationSliderSection;
